/* eslint-disable @next/next/no-img-element */
import { FC, ReactNode, useCallback, useRef } from 'react';

import { useOnClickOutside } from 'usehooks-ts';
import { useTheme } from 'styled-components';

import { Typography } from '~/common/components/Typography';
import { Icon } from '~/common/components/Icons';
import { Badge } from '~/common/components/Badge';

import {
  Container,
  ListContent,
  ListWrapper,
  NoOptionsWrapper,
  Option,
  OptionContent,
  OptionLabelSublabelWrapper,
} from './styles';
import {
  TDropdownPosition,
  TDropdownSize,
  IDropdownOption,
  TDropdownValue,
  TDropdownVariant,
  TBorderRadius,
} from '../types';

interface IDropdownOptionsProps {
  inputWrapperClientRect: DOMRect;
  containerClientRect: DOMRect;
  position: TDropdownPosition;
  isDropdownVisible: boolean;
  size: TDropdownSize;
  borderRadius?: TBorderRadius;
  noOptionsText: string;
  options: IDropdownOption[];
  isDisabled: boolean;
  onCloseDropdown: () => void;
  onSelectOption: (option: TDropdownValue) => void;
  leftCustomIcon: ReactNode;
  leftIcon: string;
  variant: TDropdownVariant;
  selectedOption: IDropdownOption;
}

export const DropdownOptions: FC<IDropdownOptionsProps> = ({
  isDropdownVisible,
  size,
  borderRadius = 'default',
  noOptionsText,
  options = [],
  isDisabled,
  onCloseDropdown,
  onSelectOption,
  leftCustomIcon,
  leftIcon,
  containerClientRect,
  inputWrapperClientRect,
  position,
  variant,
  selectedOption,
}) => {
  const theme = useTheme();

  const contentRef = useRef<HTMLDivElement>(null);

  const handleSelectOption = useCallback(
    async (option: IDropdownOption): Promise<void> => {
      const value = option?.value || '';

      onSelectOption(value);
      onCloseDropdown();
    },
    [onCloseDropdown, onSelectOption]
  );

  useOnClickOutside(contentRef, onCloseDropdown);

  return (
    <>
      <Container
        $isVisible={isDropdownVisible}
        $containerClientRect={containerClientRect}
        $position={position}
      />

      <ListWrapper
        $isVisible={isDropdownVisible}
        $inputWrapperClientRect={inputWrapperClientRect}
        $position={position}
      >
        <ListContent
          ref={contentRef}
          $isVisible={isDropdownVisible}
          $position={borderRadius === 'full' ? borderRadius : position}
        >
          {options?.length > 0 ? (
            <ul>
              {options.map((option) => {
                const optionIsDisabled = option.isDisabled || isDisabled;
                const optionIsSelected = option.value === selectedOption?.value;
                const showLabel = !!option.label && !option.hideLabel;

                return (
                  <Option
                    $size={size}
                    key={String(option.value)}
                    id={`dropdown-option-${option.value}`}
                    $isDisabled={optionIsDisabled}
                    {...(!optionIsDisabled && {
                      onClick: () => handleSelectOption(option),
                    })}
                    $variant={variant}
                    $isSelected={optionIsSelected}
                    {...(!showLabel && {
                      style: {
                        justifyContent: 'center',
                      },
                    })}
                  >
                    {option.image && (
                      <img
                        src={
                          typeof option.image === 'string'
                            ? option.image
                            : option.image?.src
                        }
                        alt={option.label}
                        style={{
                          width: '30px',
                          height: '30px',
                        }}
                      />
                    )}
                    {option.leftCustomIcon ? (
                      <>{option.leftCustomIcon}</>
                    ) : option.leftIcon ? (
                      <Icon
                        type={option.leftIcon}
                        size={40}
                        color={theme.neutral[500]}
                      />
                    ) : leftCustomIcon && variant !== 'compact' ? (
                      <>{leftCustomIcon}</>
                    ) : leftIcon && variant !== 'compact' ? (
                      <Icon
                        type={leftIcon}
                        size={40}
                        color={theme.neutral[500]}
                      />
                    ) : null}

                    {showLabel && (
                      <OptionContent>
                        <header>
                          <OptionLabelSublabelWrapper>
                            <Typography.Paragraph
                              size="small"
                              color="neutral.0"
                            >
                              {option.label}
                            </Typography.Paragraph>

                            {option.sublabel && (
                              <Typography.Paragraph
                                size="x-small"
                                color="neutral.500"
                              >
                                {option.sublabel}
                              </Typography.Paragraph>
                            )}
                          </OptionLabelSublabelWrapper>
                        </header>

                        {option.description && (
                          <Typography.Paragraph
                            size="x-small"
                            color="neutral.400"
                          >
                            {option.description}
                          </Typography.Paragraph>
                        )}
                      </OptionContent>
                    )}

                    {!!option.badge && (
                      <Badge
                        color="blue"
                        variant="lighter"
                        text={option.badge}
                        size="medium"
                        isDisabled={isDisabled}
                      />
                    )}

                    {variant !== 'compact' && (
                      <Icon
                        type="arrow-right-s-line"
                        size={20}
                        color={theme.neutral[400]}
                      />
                    )}
                  </Option>
                );
              })}
            </ul>
          ) : (
            <NoOptionsWrapper $size={size} id="select-option-empty">
              <Typography.Paragraph
                size={variant === 'compact' ? 'x-small' : 'small'}
                color="neutral.500"
              >
                {noOptionsText || 'Sem opções!'}
              </Typography.Paragraph>
            </NoOptionsWrapper>
          )}
        </ListContent>
      </ListWrapper>
    </>
  );
};
